import React from 'react'
import { Currency } from '@uniswap/sdk-core'
import { useActiveWeb3React } from '../../../hooks'
import { useWalletModalToggle } from '../../../state/application/hooks'
import { ButtonLight, ButtonPrimary } from '../../../components/Button'
import { RowBetween } from '../../../components/Row'
import { useTransactionAdder } from '../../../state/transactions/hooks'
import { useMasterChief } from '../../../hooks/useContract'
import { parseUnits, formatUnits } from 'ethers/lib/utils'
import { calculateGasMargin, getContract } from '../../../utils'
import { Text } from 'rebass'
import { BigNumber } from 'ethers'
import { MASTERCHIEF_ADDRESS } from '@hyperswap/addresses'
import { abi as MasterChief_ABI } from '../../../abis/masterchief.json'
import { ContainerStyled } from '../Stake'
import { Pair } from '@uniswap/v2-sdk'
import { useV2Pair } from 'hooks/useV2Pairs'
import CurrencyInputPanelWithoutCurrency from 'components/CurrencyInputPanelWithoutCurrency'

interface UnstakeProps {
  pId: number
  token0Currency: Currency | undefined | null
  token1Currency: Currency | undefined | null
  user: any
  setRefresh: () => void
}

const Unstake: React.FC<UnstakeProps> = ({ pId, token0Currency, token1Currency, user, setRefresh }) => {
  const masterChiefContract = useMasterChief()
  const userAmount: BigNumber | undefined = user?.amount
  const userAmountFormatted = formatUnits(userAmount ? userAmount : BigNumber.from(0), 18)
  const pair = useV2Pair(token0Currency as Currency, token1Currency as Currency)
  const validPair = pair && pair[1]

  const { account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()

  const [loadingRemove, setLoadingRemove] = React.useState<boolean>(false)

  const [tokenAmount, setTokenAmount] = React.useState<string>('')

  const addTransaction = useTransactionAdder()

  async function onWithdraw() {
    if (!chainId || !library || !account || !masterChiefContract) return
    const masterChief = getContract(MASTERCHIEF_ADDRESS, MasterChief_ABI, library, account)

    const estimate = masterChief.estimateGas.withdraw
    const method = masterChief.withdraw
    const args = [pId, parseUnits(tokenAmount, 18).toString()]

    setLoadingRemove(true)

    await estimate(...args, {})
      .then((estimatedGasLimit) =>
        method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Unstake ${token0Currency?.symbol}-${token1Currency?.symbol}`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh()
          setLoadingRemove(false)
        })
      )
      .catch((error) => {
        setLoadingRemove(false)
        console.error(error)
      })
  }
  const isDisabled =
    loadingRemove === true ||
    tokenAmount.length === 0 ||
    parseFloat(tokenAmount) <= 0 ||
    parseFloat(tokenAmount) > parseFloat(userAmountFormatted)

  return (
    <ContainerStyled>
      <CurrencyInputPanelWithoutCurrency
        value={tokenAmount}
        onUserInput={setTokenAmount}
        onMax={() => setTokenAmount(userAmount ? userAmountFormatted : '0')}
        maxValue={userAmount ? userAmountFormatted : '0'}
        id={`${token0Currency?.symbol}-${token1Currency?.symbol}-unstake`}
        showCommonBases
        smallTokenName
        pair={validPair && (validPair as Pair)}
        showMaxButton={true}
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
        ) : (
          <RowBetween>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onWithdraw()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingRemove ? 'Unstaking...' : 'Unstake'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Unstake
